<template>
  <div class="order-line-refund-modal">
    <CustomModal :show="show"
                 @close="onClose">
      <template #header>
        <h1 class="custom-modal__heading">
          View refund
        </h1>
      </template>

      <div class="order-line-refund-modal__body">
        <template v-if="loading">
          <div class="order-line-refund-modal__loading-container">
            <div class="order-line-refund-modal__loading-content">
              <h5>Loading</h5>
              <font-awesome-icon icon="spinner"
                                 class="icon"
                                 size="lg"
                                 spin></font-awesome-icon>
            </div>
          </div>
        </template>

        <template v-else>
          <template v-if="orderLine">
            <div class="order-line-refund-modal__summary">
              <div class="order-line-refund-modal__name">
                {{ orderLine.productName }}
              </div>

              <div class="order-line-refund-modal__status">
                Pending refund
              </div>
            </div>

            <div class="order-line-refund-modal__form">
              <b-form-group>
                <b-form-input
                  v-model="orderLine.refundReason"
                  type="text"
                  :disabled="true"></b-form-input>
              </b-form-group>

              <b-form-group>
                <b-form-textarea
                  v-model="orderLine.refundDetails"
                  :disabled="true"
                  rows="5"
                  max-rows="8"></b-form-textarea>
              </b-form-group>

              <b-form-group class="order-line-refund-modal__actions">
                <button
                  class="btn btn-accent-8 btn-sm btn-rounded order-line-refund-modal__close"
                  :disabled="cancelling"
                  @click="onClose">
                  <template v-if="cancelling">
                    <span class="visually-hidden">
                      Closing, please wait...
                    </span>
                    <font-awesome-icon icon="spinner"
                                       spin></font-awesome-icon>
                  </template>

                  <template v-else>
                    Close
                  </template>
                </button>

                <button
                  class="btn btn-accent-8 btn-sm btn-rounded order-line-refund-modal__cancel"
                  :disabled="loading || submitting"
                  @click="onRefundCancel">
                  <template v-if="loading || submitting">
                    <span class="visually-hidden">
                      Cancelling, please wait...
                    </span>
                    <font-awesome-icon icon="spinner"
                                       spin></font-awesome-icon>
                  </template>

                  <template v-else>
                    Cancel request
                  </template>
                </button>
              </b-form-group>
            </div>
          </template>
          <template v-else>
            <p>No product found!</p>
          </template>
        </template>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import { getRefundOrderLine, cancelRefundOrderLine } from "~/Scripts/resources/ordersResource";
import CustomModal from "~/Components/CustomModal.vue";

export default {
  name: "OrderLineRefundModal",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    getRefundOrderLine,
    CustomModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      default: null
    },
    creditOrderId: {
      type: String,
      default: null
    },
    creditOrderLineId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      orderLine: null,
      cancelling: false,
      submitting: false,
      loading: false
    };
  },
  watch: {
    show: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.cancelling = false;
          this.submitting = false;
          this.loading = true;
          this.orderLine = null;

          getRefundOrderLine( this.orderId, this.creditOrderId, this.creditOrderLineId )
            .then( result => {
              if ( result && result.data ) {
                this.orderLine = result.data;
              }
            } )
            .catch( error => {
              console.log( "an error occured when loading modal data" );
              throw error;
            } )
            .finally( () => {
              this.loading = false;
            } );
        }
      }
    }
  },
  methods: {
    onRefundCancel () {
      if ( !this.submitting ) {
        this.submitting = true;

        cancelRefundOrderLine( this.creditOrderId, this.creditOrderLineId )
          .then( result => {
            if ( result && result.data ) {
              this.$emit( "refreshPage" );
            }
          } )
          .catch( error => {
            console.log( "error cancelling the refund request" );
            throw error;
          } )
          .finally( () => {
            this.submitting = false;
          } );
      }
    },
    onClose () {
      this.orderLine = null;
      this.$emit( "close" );
    }
  }
};
</script>

<style lang="less" scoped>
.order-line-refund-modal {
  &__heading {
    color: #000;
    font-weight: 700;
    margin-bottom: 15px;
  }

  &__text {
    font-size: 14px;
  }

  &__summary {
    display: flex;
    justify-content: space-between;
    align-items: stretch;

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  &__name,
  &__status {
    font-weight: 700;
    flex-grow: 1;
    border: 0;
    padding: 10px 15px;
  }

  &__name {
    flex-basis: 70%;
  }

  &__status {
    flex-basis: 30%;
    background: #e3f3ea;
    border-left: 2px solid #80bdbf;
    border-right: 2px solid #80bdbf;
    transition: background-color 0.2s;

    &:last-child {
      border-right: 0;
      border-radius: 0 5px 0 0;
    }

    @media (max-width: 991px) {
      border-left: 0;
      border-right: 0;
      border-top: 2px solid #80bdbf;
      border-bottom: 2px solid #80bdbf;
      text-align: left;

      &:last-child {
        border-bottom: 0;
        border-radius: 0 0 5px 5px;
      }
    }
  }

  &__refund {
    background: #f1f9f4;
    border-radius: 0 5px 0 0;
    display: flex;
    justify-content: stretch;
    font-size: 14px;
    > div {
      display: flex;
      flex-grow: 1;
      align-items: center;
    }

    /deep/ label {
      flex-grow: 1;
    }

    @media (max-width: 991px) {
      border-radius: 0 0 5px 5px;
    }
  }

  &__actions {
    margin-top: 25px;
  }

  &__body {
    border: 2px solid #80bdbf;
    border-radius: 5px;
    margin-top: 25px;
  }

  &__form {
    padding: 30px 10px 0 10px;
  }

  &__close {
    margin-right: 5px;

    @media (max-width: 368px) {
      margin-bottom: 5px;
    }
  }

  &__cancel {
    background-color: #dc3545;
    &:hover {
      background: darken(#dc3545, 10%);
    }
  }

  @media (max-width: 991px) {
    /deep/ .custom-modal__content--md {
      width: 80%;
    }
  }
}
</style>
